/**
 * Book API (ebook-plan § 3.6): the state, one chapter at a time, the reading position, and the
 * phase-B purchase (consent, Stripe Checkout, verify), the lukaaji.com code and claim, and the
 * owner downloads. Every response is checked against the contract (bookContract.ts) before the
 * UI sees it.
 */
import { apiClient, getToken } from './client';
import {
  bookChapterSchema,
  bookFilesSchema,
  bookStateSchema,
  checkoutOutSchema,
  consentInSchema,
  consentOutSchema,
  ownedOutSchema,
  progressInSchema,
  verifyOutSchema,
  type BookChapter,
  type BookFiles,
  type BookState,
  type ConsentIn,
  type ProgressIn,
  type VerifyOut,
} from './bookContract';

export const bookApi = {
  getState: async (): Promise<BookState> => {
    const response = await apiClient.get<unknown>('/book');
    return bookStateSchema.parse(response.data);
  },

  getChapter: async (chapterId: string): Promise<BookChapter> => {
    const response = await apiClient.get<unknown>(`/book/chapters/${encodeURIComponent(chapterId)}`);
    return bookChapterSchema.parse(response.data);
  },

  saveProgress: async (progress: ProgressIn): Promise<void> => {
    await apiClient.put('/book/progress', progressInSchema.parse(progress));
  },

  /**
   * The same write for a page that is being hidden or closed: `fetch` with `keepalive` outlives
   * the page, and unlike `sendBeacon` it can carry the bearer header (§ 3.13). Never throws.
   */
  saveProgressOnHide: (progress: ProgressIn): void => {
    const token = getToken();
    const parsed = progressInSchema.safeParse(progress);
    if (!token || !parsed.success || typeof fetch !== 'function') return;
    try {
      void fetch(`${apiClient.defaults.baseURL ?? '/api'}/book/progress`, {
        method: 'PUT',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(parsed.data),
      }).catch(() => undefined);
    } catch {
      // A browser that refuses the keepalive request: the debounced save already ran or runs next time.
    }
  },

  /** Records the consent the user ticked; `textSha256` is of the exact string rendered (CR-5). */
  recordConsent: async (consent: ConsentIn): Promise<string> => {
    const response = await apiClient.post<unknown>('/book/consent', consentInSchema.parse(consent));
    return consentOutSchema.parse(response.data).consentId;
  },

  /** A Stripe Checkout Session for that consent (or the account's open one): its https URL. */
  createCheckout: async (consentId: string): Promise<string> => {
    const response = await apiClient.post<unknown>('/book/checkout', { consentId });
    return checkoutOutSchema.parse(response.data).url;
  },

  /** The success page: fulfils the caller's own paid session (the webhook may already have). */
  verifyCheckout: async (sessionId: string): Promise<VerifyOut> => {
    const response = await apiClient.post<unknown>('/book/checkout/verify', { sessionId });
    return verifyOutSchema.parse(response.data);
  },

  /** A lukaaji.com order's code (single use). */
  redeem: async (code: string): Promise<boolean> => {
    const response = await apiClient.post<unknown>('/book/redeem', { code });
    return ownedOutSchema.parse(response.data).owned;
  },

  /** A lukaaji.com order for this account's verified email address. */
  claim: async (): Promise<boolean> => {
    const response = await apiClient.post<unknown>('/book/claim', {});
    return ownedOutSchema.parse(response.data).owned;
  },

  /** The owner's download links, each signed for 5 minutes: fetch right before use. */
  getFiles: async (): Promise<BookFiles> => {
    const response = await apiClient.get<unknown>('/book/files');
    return bookFilesSchema.parse(response.data);
  },
};
